<template>
  <div :class="$style.productInformations">
    <div :class="$style.title">
      <lv-heading tag="h3" level="3" version2>
        {{ $awt('aw.common.product.informations.accordion.ingredients') }}
      </lv-heading>
    </div>
    <!-- region Ingredients -->
    <div :class="$style.separator">
      <div v-if="showIngredientsBlock && ingredients">
        <p v-html="ingredients" />
        <div v-html="$awt('aw.common.product.details.general_info')" />
      </div>
      <div v-if="showIngredientsDetailedBlock && ingredientsDetailed?.length" :class="$style.details">
        <div v-for="({ position, name }, idx) in ingredientsDetailed" :key="`${position}-${idx}`" :class="$style.detailsItem">
          <span v-html="name" />
        </div>
        <div v-html="$awt('aw.common.product.details.general_info')" />
      </div>
      <aw-svg-border is-horizontal :class="$style.svgBorder" />
    </div>
    <!-- endregion Ingredients -->
    <!-- region Allergens -->
    <div v-if="(showAllergensDetailedBlock && allergensDetailed) || (showAllergensBlock && allergenInfo)" :class="$style.separator">
      <div :class="$style.headingMedium">
        <lv-heading tag="h4" level="4" version2>
          {{ $awt('aw.common.product.informations.accordion.allergens.title') }}
        </lv-heading>
      </div>
      <!-- region Allergens0 -->
      <div v-if="showAllergensDetailedBlock && allergensDetailed" :class="$style.details">
        <div v-for="({ position, name, icon }, idx) in allergensDetailed" :key="`${position}-${idx}`" :class="$style.detailsItem">
          <span :class="$style.detailsIcon" v-html="icon" />
          <span v-html="name" />
        </div>
      </div>
      <!-- endregion Allergens0 -->
      <!-- region Allergens1 -->
      <div v-if="showAllergensBlock && allergenInfo">
        <!-- region Contains -->
        <div v-if="allergenInfo.contains">
          <div v-for="(contain, i) in allergenInfo.contains" :key="i">
            <p v-html="contain" />
          </div>
        </div>
        <!-- endregion Contains -->
        <br>
        <!-- region May contains -->
        <div v-if="allergenInfo.mayContains">
          <div :class="$style.headingMedium">
            <lv-heading tag="h4" level="4" version2>
              {{ $awt('aw.common.product.informations.accordion.allergens.may_contains') }}
            </lv-heading>
          </div>
          <div v-for="(mayContain, i) in allergenInfo.mayContains" :key="i">
            <p v-html="mayContain" />
          </div>
        </div>
        <!-- endregion May contains -->
      </div>
      <!-- endregion Allergens1 -->
      <aw-svg-border is-horizontal :class="$style.svgBorder" />
    </div>
    <!-- endregion Allergens -->
    <!-- region Nutritions -->
    <div v-if="showNutritionBlock && nutritionsTable?.data?.length" :class="$style.separator">
      <div :class="$style.headingMedium">
        <lv-heading tag="h4" level="4" version2>
          <span v-text="$awt('aw.common.product.informations.accordion.nutritions')" />
        </lv-heading>
      </div>
      <client-only>
        <aw-data-table
          apply-default-margin
          :column-config="nutritionsTable.header"
          initial-column="1"
          @data-table-load-data="fetchNextPage"
        >
          <template #row="{ data, table }">
            <tr :class="table.style.row">
              <template v-for="(col, idx) in data" :key="idx">
                <td v-if="table.isSelectedColumn(`${idx}`)" :class="table.style.cell">
                  <div v-if="table.isCompact" :class="$style.tableHead" v-text="data[0]" />
                  <div :class="{[$style.tableHeadFirst]: idx === 0}" v-text="col" />
                </td>
              </template>
            </tr>
          </template>
        </aw-data-table>
      </client-only>
      <div>
        {{ $awt('aw.common.product.informations.accordion.nutritions_footer') }}
      </div>
      <aw-svg-border is-horizontal :class="$style.svgBorder" />
    </div>
    <!-- endregion Nutritions -->
  </div>
</template>

<script>
  import { defineAsyncComponent } from 'vue';
  import { mapState } from 'pinia';
  import {
    LvHeading,
  } from '~~/common/components/loginet-vue-shop/index';
  import AwSvgBorder from '~~/common/components/AwSvgBorder.vue';
  import productDataMixin from '~~/common/mixins/productDataMixin';
  import { useProductStore } from '~~/common/stores/product';

  export default {
    name: 'AwProductInformations',
    components: {
      LvHeading,
      AwSvgBorder,
      AwDataTable: defineAsyncComponent(() => import('~~/common/components/Common/AwDataTable')),
    },
    mixins: [
      productDataMixin,
    ],
    computed: {
      ...mapState(useProductStore, {
        product: state => state.data,
      }),
      ingredients () {
        return this.getVariant?.ingredients?.description;
      },
      ingredientsDetailed () {
        return this.getVariant?.ingredientsDetailed?.ingredientsDetailed;
      },
      allergenInfo () {
        return this.getVariant?.allergens?.allergenInfo;
      },
      allergensDetailed () {
        return this.getVariant?.allergensDetailed?.allergensDetailed;
      },
      nutritions () {
        return this.getVariant?.nutrition?.nutritions;
      },
      nutritionsTable () {
        const nutr = this.nutritions;
        if (!nutr || typeof nutr !== 'object') {
          return null;
        }
        const columnCount = Math.max((nutr.data[0]?.length || 0), (nutr.header?.length || 0));
        const header = [
          ...new Array(columnCount).fill(null).map((_, i) => ({
            value: `${i}`,
            rawLabel: nutr.header?.[i] || '',
            next: `${i + 1}`,
            prev: `${i - 1}`,
          })),
        ];
        header[header.length - 1].next = null;
        header[0].prev = null;
        header[1].prev = null;
        const data = nutr.data || [];
        return {
          header,
          data,
        };
      },
    },
    mounted () {
      const productStore = useProductStore();
      if (this.showNutritionBlock) {
        productStore.fetchNutrition({
          id: this.productId,
          variantId: this.variantId,
        });
      }

      if (this.showAllergensDetailedBlock) {
        productStore.fetchAllergensDetailed({
          id: this.productId,
          variantId: this.variantId,
        });
      }

      if (this.showAllergensBlock) {
        productStore.fetchAllergens({
          id: this.productId,
          variantId: this.variantId,
        });
      }

      if (this.showIngredientsBlock) {
        productStore.fetchIngredients({
          id: this.productId,
          variantId: this.variantId,
        });
      }
      if (this.showIngredientsDetailedBlock) {
        productStore.fetchIngredientsDetailed({
          id: this.productId,
          variantId: this.variantId,
        });
      }
    },
    methods: {
      fetchNextPage (context) {
        context.appendData({
          data: this.nutritionsTable.data,
          more: false,
        });
      },
    },
  };
</script>

<style module lang="scss" rel="stylesheet/scss">
.title {
  margin-bottom: 40px;

  @include mobile(max) {
    display: none;
  }
}

.separator:last-child {
  .svgBorder {
    display: none;
  }
}

.svgBorder {
  margin-top: 20px;

  @include tablet(min) {
    margin-top: 40px;
  }
}

.headingMedium {
  margin: 20px 0;
}

.details {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.detailsItem {
  font-size: 16px;
  line-height: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border: 1px solid $color-border-disabled;
  border-radius: 12px;
  background: $color-background-3;

  @include tablet(min) {
    align-items: center;
    flex-direction: row;
    gap: 16px;
  }
}

.detailsIcon {
  width: 40px;
  height: 40px;
  padding: 8px;
  border: 1px solid $color-border;
  border-radius: 16px;
  background: $color-white;
}

.tableHeadFirst,
.tableHead {
  font-weight: 900;
}

.tableHead {
  margin-bottom: 4px;
}

.productInformations {
  @include mobile(max) {
    padding-top: 20px;
  }
}
</style>
